import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, CircularProgress, Box, List, ListItem, ListItemButton, ListItemText, useTheme, useMediaQuery } from '@mui/material';
import ObraEditModal from '../ObraEditModal';
import LancamentoEditModal from '../LancamentoEditModal';
import ValoresCorrigidosModal from '../ValoresCorrigidosModal';
import { getAnexoUrl } from '../../services/api';
import { Obra } from '../../services/types';

interface ObraModalsProps {
    obra: Obra | null;
    modalsState: any; // Type this properly if possible, or use the return type of useObraModals
    actions: any; // Type this properly
    refetchDetails: () => void;
    valoresCorrigidos: any;
    recalculateCorrecao: () => void;
    setRefreshLancamentosTrigger: React.Dispatch<React.SetStateAction<number>>;
}

export const ObraModals: React.FC<ObraModalsProps> = ({
    obra,
    modalsState,
    actions,
    refetchDetails,
    valoresCorrigidos,
    recalculateCorrecao,
    setRefreshLancamentosTrigger
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const {
        isEditModalOpen, setIsEditModalOpen,
        isDeleteModalOpen, setIsDeleteModalOpen,
        isValoresCorrigidosModalOpen, setIsValoresCorrigidosModalOpen,
        isLancamentoEditModalOpen, setIsLancamentoEditModalOpen,
        isAttachmentsModalOpen, setIsAttachmentsModalOpen,
        selectedAttachments,
        lancamentoToEdit,
        lancamentoEditInitialStep
    } = modalsState;

    const {
        isDeleting,
        handleDeleteObra
    } = actions;

    if (!obra) return null;

    return (
        <>
            <ObraEditModal
                obra={obra}
                open={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onObraUpdated={refetchDetails}
            />

            <LancamentoEditModal
                lancamento={lancamentoToEdit}
                initialStep={lancamentoEditInitialStep}
                open={isLancamentoEditModalOpen}
                onClose={() => setIsLancamentoEditModalOpen(false)}
                onLancamentoUpdated={() => {
                    setRefreshLancamentosTrigger(prev => prev + 1);
                    refetchDetails();
                }}
            />

            <Dialog
                open={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                fullScreen={fullScreen}
            >
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Você tem certeza que deseja excluir a obra "{obra.nome}"? Todos os dados associados serão removidos.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>Cancelar</Button>
                    <Button onClick={handleDeleteObra} color="error" disabled={isDeleting}>
                        {isDeleting ? <CircularProgress size={24} /> : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isAttachmentsModalOpen}
                onClose={() => setIsAttachmentsModalOpen(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={fullScreen}
            >
                <DialogTitle>Anexos do Lançamento</DialogTitle>
                <DialogContent>
                    {selectedAttachments.length > 0 ? (
                        <List>
                            {selectedAttachments.map((anexo: any, index: number) => (
                                <ListItem key={index} disablePadding>
                                    <ListItemButton component="a" href={getAnexoUrl(anexo.caminho_arquivo)} target="_blank" rel="noopener noreferrer">
                                        <ListItemText primary={anexo.nome_original || `Anexo ${index + 1}`} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Box sx={{ p: 2 }}>Nenhum anexo encontrado.</Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsAttachmentsModalOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            {valoresCorrigidos && (
                <ValoresCorrigidosModal
                    open={isValoresCorrigidosModalOpen}
                    onClose={() => setIsValoresCorrigidosModalOpen(false)}
                    detalhamento={valoresCorrigidos.detalhamento}
                    valorCorrigido={valoresCorrigidos.valorCorrigido}
                    onRatesUpdated={recalculateCorrecao}
                />
            )}
        </>
    );
};
